import { Trans } from '@lingui/react/macro'
import { createColumnHelper } from '@tanstack/react-table'
import { checkbox } from '@tecsinapse/cortex-core'
import { Tooltip } from '@tecsinapse/cortex-react'
import React from 'react'
import { GoPerson } from 'react-icons/go'
import { LiaFileAlt, LiaFolder } from 'react-icons/lia'
import { MdOutlineCalendarMonth } from 'react-icons/md'
import { i18n } from '@/i18n'
import { ItemBiblioteca } from '@/core/types/graphql'
import { truncate } from '@/core/utils/truncate'
import { ColumnsProps } from '../types/types'
import { BiLock, BiWorld } from 'react-icons/bi'
import { t } from '@lingui/core/macro'
import { NewItemBadge } from './new-item-badge'
import { isNewItem } from '../utils/functions'

const columnHelper = createColumnHelper<ItemBiblioteca>()

export const columnsPastas = ({
  isItemSelected,
  onClickItem,
  selectedRows,
  isMobile
}: ColumnsProps) => {
  if (isMobile) {
    return [
      columnHelper.display({
        id: 'mobileData',
        cell: ({ row: { original: item } }) => {
          const permissoes = (item?.permissoes ?? [])
            .map(permissao => permissao?.grupoUsuarios.nome)
            .join(', ')

          return (
            <div className='space-y-small flex flex-row items-center gap-4'>
              <input
                className={checkbox()}
                type='checkbox'
                checked={isItemSelected(selectedRows, item.id)}
                onChange={() => onClickItem(item)}
              />
              <div>
                {/* Linha do Checkbox, Ícone e Nome */}
                <div className='flex items-center space-x-micro'>
                  {item.isPasta ? (
                    <LiaFolder className='text-primary-medium text-deca' />
                  ) : (
                    <LiaFileAlt className='text-primary-medium text-deca' />
                  )}
                  <p className='text-h5 font-bold text-secondary-xdark'>
                    {item.nome}
                  </p>
                  {item.isPublico ? (
                    <Tooltip text={t`Pasta Pública`}>
                      <BiWorld className='text-primary-medium' />
                    </Tooltip>
                  ) : (
                    <Tooltip text={t`Pasta Privada`}>
                      <BiLock className='text-primary-medium' />
                    </Tooltip>
                  )}
                </div>

                {/* Data de Criação */}
                <div className='flex items-center space-x-micro'>
                  <MdOutlineCalendarMonth
                    className='text-primary-medium'
                    size={16}
                  />
                  <p className='text-sub text-secondary-xdark gap-0.5 flex'>
                    <span className='font-bold'>
                      <Trans>Criação:</Trans>
                    </span>
                    {i18n.date(item.dataCriacao, { dateStyle: 'short' })}
                  </p>
                </div>

                {/* Criado Por */}
                <div className='flex items-center space-x-micro'>
                  <GoPerson className='text-primary-medium' />
                  <p className='text-sub text-secondary-xdark gap-0.5 flex'>
                    <span className='font-bold'>
                      <Trans>Criado por:</Trans>
                    </span>
                    {item?.proprietario?.nome}
                  </p>
                </div>

                {/* Permissões de Acesso */}
                {permissoes.length > 0 && item.isPasta ? (
                  <div className='flex items-center space-x-micro'>
                    <Tooltip text={permissoes}>
                      <p className='text-sub text-secondary-xdark gap-0.5 flex'>
                        <span className='font-bold'>
                          <Trans>Permissão de Acesso:</Trans>
                        </span>
                        {truncate(permissoes, 25)}{' '}
                        {/* Ajuste o truncamento conforme necessário */}
                      </p>
                    </Tooltip>
                  </div>
                ) : (
                  <p className={'font-bold text-sub text-secondary-xdark'}>
                    <Trans>Sem permissões de acesso</Trans>
                  </p>
                )}
                <NewItemBadge dataCriacao={item.dataCriacao} />
              </div>
            </div>
          )
        }
      })
    ]
  }

  // Colunas para Desktop
  return [
    columnHelper.display({
      id: 'check',
      cell: ({ row: { original: item } }) => (
        <input
          className={checkbox()}
          type='checkbox'
          checked={isItemSelected(selectedRows, item.id)}
          onChange={() => onClickItem(item)}
        />
      )
    }),
    columnHelper.display({
      id: 'nome',
      cell: ({ row: { original: item } }) => (
        <div className='flex items-center space-x-micro'>
          {item.isPasta ? (
            <LiaFolder className='text-primary-medium text-deca' />
          ) : (
            <LiaFileAlt className='text-primary-medium text-deca' />
          )}
          <Tooltip text={item.nome}>
            <p className='font-bold text-h5 text-secondary-xdark'>
              {truncate(item.nome)}
            </p>
          </Tooltip>
          {item.isPublico ? (
            <Tooltip text={t`Pasta Pública`}>
              <BiWorld className='text-primary-medium' />
            </Tooltip>
          ) : (
            <Tooltip text={t`Pasta Privada`}>
              <BiLock className='text-primary-medium' />
            </Tooltip>
          )}
        </div>
      )
    }),
    columnHelper.display({
      id: 'date',
      cell: ({ row: { original: item } }) => (
        <div className='flex items-center space-x-micro'>
          <MdOutlineCalendarMonth className='text-primary-medium' size={16} />
          <p className='text-sub text-secondary-xdark gap-0.5 flex'>
            <span className='font-bold'>
              <Trans>Criação:</Trans>
            </span>
            {i18n.date(item.dataCriacao, { dateStyle: 'short' })}
          </p>
        </div>
      )
    }),
    columnHelper.display({
      id: 'name',
      cell: ({ row: { original: item } }) => (
        <div className='flex items-center space-x-micro'>
          <GoPerson className='text-primary-medium' />
          <p className='text-sub text-secondary-xdark gap-0.5 flex'>
            <span className='font-bold'>
              <Trans>Criado por:</Trans>
            </span>
            {item?.proprietario?.nome}
          </p>
        </div>
      )
    }),
    columnHelper.display({
      id: 'attached',
      cell: ({ row: { original: item } }) => {
        if (!item.isPasta) return null

        const permissoes = (item?.permissoes ?? [])
          .map(permissao => permissao?.grupoUsuarios.nome)
          .join(', ')

        return (
          <>
            {permissoes.length > 0 ? (
              <Tooltip text={permissoes} style={{ maxWidth: 400 }}>
                <p className='text-sub text-secondary-xdark gap-0.5 flex'>
                  <span className='font-bold'>
                    <Trans>Permissão de Acesso:</Trans>
                  </span>
                  {truncate(permissoes)}
                </p>
              </Tooltip>
            ) : (
              <p className={'font-bold text-sub text-secondary-xdark'}>
                <Trans>Sem permissões de acesso</Trans>
              </p>
            )}
          </>
        )
      }
    }),
    columnHelper.display({
      id: 'novo',
      cell: ({ row: { original: item } }) => (
        <div className='flex items-center space-x-micro'>
          <NewItemBadge dataCriacao={item.dataCriacao} />
        </div>
      )
    })
  ]
}
